"use client";
import React, { ReactNode } from "react";

import { Input } from "@nextui-org/react";
import { useTranslations } from "next-intl";

import FormWrapper from "@/ui/Forms/FormWrapper";
import { replaceInputValue } from "@/utils/replaceInputValue";

export interface FormRangeInputProps {
  minValue: number | undefined;
  maxValue: number | undefined;
  onChangeMin: (value: number | undefined) => void;
  onChangeMax: (value: number | undefined) => void;
  label?: string;
  error?: string;
  unit?: ReactNode;
  required?: boolean;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  decimal?: boolean;
  labelRightComponent?: ReactNode;
  errorClassName?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Form range input for "from-to" range values with optional unit
 * Used for values like price range, area range, etc.
 */
export const FormRangeInput: React.FC<FormRangeInputProps> = ({
  minValue,
  maxValue,
  onChangeMin,
  onChangeMax,
  label,
  error,
  unit,
  required = false,
  minPlaceholder,
  maxPlaceholder,
  decimal = true,
  labelRightComponent,
  errorClassName,
  className,
  disabled = false,
}) => {
  const t = useTranslations();

  const handleChangeMin = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value) {
      onChangeMin(undefined);
      return;
    }

    let processedValue = replaceInputValue(value);

    if (!decimal && processedValue.includes(".")) {
      processedValue = processedValue.split(".")[0];
    }

    onChangeMin(processedValue === "" ? undefined : Number(processedValue));
  };

  const handleChangeMax = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value) {
      onChangeMax(undefined);
      return;
    }

    let processedValue = replaceInputValue(value);

    if (!decimal && processedValue.includes(".")) {
      processedValue = processedValue.split(".")[0];
    }

    onChangeMax(processedValue === "" ? undefined : Number(processedValue));
  };

  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      <div className="flex items-center gap-[1rem] w-full">
        <div className="flex-1">
          <Input
            value={minValue?.toString() ?? ""}
            placeholder={minPlaceholder || t("from")}
            onChange={handleChangeMin}
            inputMode={decimal ? "decimal" : "numeric"}
            isInvalid={!!error}
            isDisabled={disabled}
            classNames={{
              base: "form-number-input-base",
              mainWrapper: "form-number-input-wrapper",
              input: "form-number-input",
              innerWrapper: "form-number-input-wrapper",
              inputWrapper: "form-number-input-container",
              errorMessage: "form-number-input-error",
            }}
            className={className}
          />
        </div>

        <div className="flex items-center justify-center text-[1.5rem] font-medium text-input-text">
          -
        </div>

        <div className="flex-1">
          <Input
            value={maxValue?.toString() ?? ""}
            placeholder={maxPlaceholder || t("to")}
            onChange={handleChangeMax}
            inputMode={decimal ? "decimal" : "numeric"}
            isInvalid={!!error}
            isDisabled={disabled}
            classNames={{
              base: "form-number-input-base",
              mainWrapper: "form-number-input-wrapper",
              input: "form-number-input",
              innerWrapper: "form-number-input-wrapper",
              inputWrapper: "form-number-input-container",
              errorMessage: "form-number-input-error",
            }}
            className={className}
          />
        </div>

        {unit && <div className="form-number-input-unit">{unit}</div>}
      </div>
    </FormWrapper>
  );
};

export default FormRangeInput;
